import React from 'react';
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react';
import { cn } from '../../../utils/cn';

/**
 * GlassAlert - Executive design system alert component
 *
 * A premium notification alert with glass morphism effects and semantic variants.
 *
 * @example
 * ```tsx
 * <GlassAlert
 *   variant="success"
 *   title="Success!"
 *   message="Your changes have been saved."
 *   onClose={() => setShowAlert(false)}
 * />
 * ```
 */

export interface GlassAlertProps {
  /** Alert variant */
  variant: 'success' | 'warning' | 'error' | 'info';
  /** Alert title */
  title?: string;
  /** Alert message */
  message: string;
  /** Dismiss callback */
  onClose?: () => void;
  /** Show dismiss button */
  dismissible?: boolean;
  /** Custom icon */
  icon?: React.ReactNode;
  /** Additional actions */
  actions?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

const variantConfig = {
  success: {
    icon: CheckCircle,
    bgClass: 'bg-emerald-50/80 dark:bg-emerald-950/40',
    borderClass: 'border-emerald-200/60 dark:border-emerald-800/40',
    iconClass: 'text-emerald-500',
    titleClass: 'text-emerald-800 dark:text-emerald-200',
    messageClass: 'text-emerald-700 dark:text-emerald-300',
    glowClass: 'shadow-[0_0_20px_-4px_rgba(16,185,129,0.2)]',
  },
  warning: {
    icon: AlertTriangle,
    bgClass: 'bg-amber-50/80 dark:bg-amber-950/40',
    borderClass: 'border-amber-200/60 dark:border-amber-800/40',
    iconClass: 'text-amber-500',
    titleClass: 'text-amber-800 dark:text-amber-200',
    messageClass: 'text-amber-700 dark:text-amber-300',
    glowClass: 'shadow-[0_0_20px_-4px_rgba(245,158,11,0.2)]',
  },
  error: {
    icon: AlertCircle,
    bgClass: 'bg-rose-50/80 dark:bg-rose-950/40',
    borderClass: 'border-rose-200/60 dark:border-rose-800/40',
    iconClass: 'text-rose-500',
    titleClass: 'text-rose-800 dark:text-rose-200',
    messageClass: 'text-rose-700 dark:text-rose-300',
    glowClass: 'shadow-[0_0_20px_-4px_rgba(244,63,94,0.2)]',
  },
  info: {
    icon: Info,
    bgClass: 'bg-blue-50/80 dark:bg-blue-950/40',
    borderClass: 'border-blue-200/60 dark:border-blue-800/40',
    iconClass: 'text-blue-500',
    titleClass: 'text-blue-800 dark:text-blue-200',
    messageClass: 'text-blue-700 dark:text-blue-300',
    glowClass: 'shadow-[0_0_20px_-4px_rgba(59,130,246,0.2)]',
  },
};

export const GlassAlert: React.FC<GlassAlertProps> = ({
  variant,
  title,
  message,
  onClose,
  dismissible = true,
  icon,
  actions,
  className,
}) => {
  const config = variantConfig[variant];
  const IconComponent = config.icon;

  return (
    <div
      className={cn(
        'relative flex gap-3 p-4 rounded-xl',
        'backdrop-blur-sm border',
        config.bgClass,
        config.borderClass,
        config.glowClass,
        'animate-in slide-in-from-top-2 duration-300',
        className
      )}
      role="alert"
    >
      {/* Icon */}
      <div className={cn('flex-shrink-0 mt-0.5', config.iconClass)}>
        {icon || <IconComponent className="w-5 h-5" />}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {title && (
          <h4 className={cn('text-sm font-semibold mb-1', config.titleClass)}>
            {title}
          </h4>
        )}
        <p className={cn('text-sm', config.messageClass)}>{message}</p>

        {/* Actions */}
        {actions && <div className="mt-3 flex items-center gap-2">{actions}</div>}
      </div>

      {/* Dismiss button */}
      {dismissible && onClose && (
        <button
          onClick={onClose}
          className={cn(
            'flex-shrink-0 p-1 rounded-lg',
            'transition-colors duration-200',
            'hover:bg-white/40 dark:hover:bg-slate-700/40',
            config.iconClass
          )}
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

GlassAlert.displayName = 'GlassAlert';

/**
 * GlassAlertBanner - Full-width alert banner variant
 */
export interface GlassAlertBannerProps extends Omit<GlassAlertProps, 'className'> {
  /** Sticky position at top */
  sticky?: boolean;
}

export const GlassAlertBanner: React.FC<GlassAlertBannerProps> = ({
  sticky = false,
  ...props
}) => {
  return (
    <GlassAlert
      {...props}
      className={cn('rounded-none border-x-0', sticky && 'sticky top-0 z-50')}
    />
  );
};

GlassAlertBanner.displayName = 'GlassAlertBanner';
